import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { Users, Plus, Trash2, Shield, ShieldCheck, Mail, Lock, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

export const AdminsPage = () => {
  const { user } = useContext(AuthContext);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [creating, setCreating] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    try {
      const res = await api.get('/auth/admins');
      setAdmins(res.data);
    } catch (error) {
      console.error('Error loading admins:', error);
      if (error.response?.status === 403) {
        toast.error('Accesso non autorizzato');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!newAdmin.email || !newAdmin.password || !newAdmin.name) {
      toast.error('Compila tutti i campi');
      return;
    }

    setCreating(true);
    try {
      await api.post('/auth/admins', newAdmin);
      toast.success('Admin creato con successo');
      setIsDialogOpen(false);
      setNewAdmin({ email: '', password: '', name: '' });
      loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    try {
      await api.delete(`/auth/admins/${adminId}`);
      toast.success('Admin eliminato');
      loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleToggle2FA = async (adminId) => {
    try {
      const res = await api.put(`/auth/admins/${adminId}/toggle-2fa`);
      toast.success(`2FA ${res.data.two_factor_enabled ? 'attivata' : 'disattivata'}`);
      loadAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 8) {
      toast.error('La password deve avere almeno 8 caratteri');
      return;
    }
    setResetting(true);
    try {
      await api.put(`/auth/admins/${resetTarget.id}/reset-password`, { new_password: resetPassword });
      toast.success(`Password aggiornata per ${resetTarget.name}`);
      setResetTarget(null);
      setResetPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il reset');
    } finally {
      setResetting(false);
    }
  };

  // Check if current user is super admin
  if (!user?.is_super_admin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="glass border-white/10 max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Accesso Limitato</h2>
            <p className="text-muted-foreground">
              Solo il Super Admin può accedere a questa sezione.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admins-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Users className="h-8 w-8 text-primary" />
            Gestione Admin
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestisci gli amministratori della piattaforma
          </p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-admin-btn">
              <Plus className="h-4 w-4" />
              Nuovo Admin
            </Button>
          </DialogTrigger>
          <DialogContent className="glass border-white/10">
            <DialogHeader>
              <DialogTitle>Crea Nuovo Admin</DialogTitle>
              <DialogDescription>
                Aggiungi un nuovo amministratore alla piattaforma.
                La 2FA via email sarà attivata di default.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  placeholder="Mario Rossi"
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                  className="glass"
                  data-testid="new-admin-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@esempio.com"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  className="glass"
                  data-testid="new-admin-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  className="glass"
                  data-testid="new-admin-password"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleCreateAdmin} disabled={creating} data-testid="create-admin-btn">
                {creating ? 'Creazione...' : 'Crea Admin'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Admins Table */}
      <Card className="glass border-white/10">
        <CardHeader>
          <CardTitle>Amministratori</CardTitle>
          <CardDescription>
            Lista di tutti gli utenti con accesso al pannello admin
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Caricamento...
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utente</TableHead>
                  <TableHead>Ruolo</TableHead>
                  <TableHead>2FA</TableHead>
                  <TableHead>Data Creazione</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.id} data-testid={`admin-row-${admin.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="text-primary font-semibold">
                            {admin.name?.charAt(0)?.toUpperCase() || 'A'}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium">{admin.name}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {admin.email}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {admin.is_super_admin ? (
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                          <ShieldCheck className="h-3 w-3 mr-1" />
                          Super Admin
                        </Badge>
                      ) : (
                        <Badge variant="outline">
                          <Shield className="h-3 w-3 mr-1" />
                          Admin
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={admin.two_factor_enabled !== false}
                          onCheckedChange={() => handleToggle2FA(admin.id)}
                          disabled={admin.is_super_admin}
                          data-testid={`2fa-toggle-${admin.id}`}
                        />
                        <Lock className={`h-4 w-4 ${admin.two_factor_enabled !== false ? 'text-green-500' : 'text-muted-foreground'}`} />
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(admin.created_at).toLocaleDateString('it-IT')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-primary"
                          title="Reset password"
                          onClick={() => { setResetTarget(admin); setResetPassword(''); }}
                          data-testid={`reset-password-${admin.id}`}
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        {!admin.is_super_admin && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-admin-${admin.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="glass border-white/10">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Elimina Admin</AlertDialogTitle>
                              <AlertDialogDescription>
                                Sei sicuro di voler eliminare <strong>{admin.name}</strong>?
                                Questa azione non può essere annullata.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annulla</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteAdmin(admin.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Elimina
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(open) => { if (!open) { setResetTarget(null); setResetPassword(''); } }}>
        <DialogContent className="glass border-white/10" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Imposta una nuova password per <strong>{resetTarget?.name}</strong> ({resetTarget?.email}).
              L'utente potrà usare immediatamente la nuova password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reset-password">Nuova password</Label>
              <Input
                id="reset-password"
                type="password"
                placeholder="Almeno 8 caratteri"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                className="glass"
                autoComplete="new-password"
                data-testid="reset-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetTarget(null); setResetPassword(''); }}>
              Annulla
            </Button>
            <Button onClick={handleResetPassword} disabled={resetting} data-testid="confirm-reset-password-btn">
              {resetting ? 'Aggiornamento...' : 'Aggiorna password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Info Card */}
      <Card className="glass border-white/10 border-amber-500/20">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <ShieldCheck className="h-10 w-10 text-amber-400 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-400">Autenticazione a Due Fattori (2FA)</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Tutti gli admin hanno la 2FA attiva di default. Ad ogni login verrà inviato un codice 
                di verifica via email. Il Super Admin non può disattivare la propria 2FA.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminsPage;
